import { memo } from 'react';

import clsx from 'clsx';
import { Badge } from '@mui/material';
import { ToastContainer } from 'react-toastify';

import { BsCameraVideo, BsCameraVideoOffFill, BsMic, BsMicMuteFill } from 'react-icons/bs';
import { HiOutlineMicrophone } from 'react-icons/hi';
import { IoAlert } from 'react-icons/io5';
import { MdWaves } from 'react-icons/md';
import { PiSpeakerHigh } from 'react-icons/pi';

import { InfoModal, BackgroundEffectsSimpleModal, AvatarCircle } from '@src/components';
import { OButton, OInput, OSelect, OTooltip } from '@src/shared';

import { LoaderIcon } from '@src/assets/icons';

import { useWebinarLobby } from '@pages/Webinar/hooks';

import { formatDateWithCity } from '@src/utils';

import styles from '../WebinarLobby.module.scss';

const date = new Date();

const WebinarLobbyWeb = () => {
  const {
    roomTitle,
    isHost,
    isPasswordRequired,
    isWebinarStartedByHost,
    errors,
    password,
    peerName,
    inputValue,
    joinErrors,
    oSelectSize,
    originalStream,
    isAudioAllowed,
    isVideoAllowed,
    speakerDevices,
    selectedAudio,
    selectedCamera,
    selectedSpeaker,
    videoInputDevices,
    audioInputDevices,
    handleAudio,
    handleVideo,
    handleChangeMediaDevice,
    handleChangeUsername,
    handleChangePassword,
    setVideoRef,
    isBackgroundEffectsOpen,
    isApplyButtonDisabled,
    selectedBackgroundEffect,
    isSubmitButtonDisabled,
    customTooltipStyle,
    onSelectBackgroundEffect,
    handleOpenEffectsModal,
    removeBackgroundEffect,
    handleApplyChanges,
    handleCloseModal,
    handleJoinWebinar,
    isMicClosed,
    isCamClosed,
    isJoinLoading,
  } = useWebinarLobby();

  return (
    <div className={styles.webinarLobbyContainer}>
      <div className={styles.lobbyLeftSide}>
        <div className={styles.settingsWrapper}>
          <div className={styles.previewBodyContainer}>
            <div className={styles.mediaPreviewWrapper}>
              <div className={styles.mediaIconsWrapper}>
                {/* Mic */}
                <OTooltip
                  isCapitalized
                  title={`${!isMicClosed ? 'mute' : 'unmute'} `}
                  customStyles={customTooltipStyle}
                >
                  <div
                    className={clsx(styles.iconWrapper, !isMicClosed ? styles.transparentBg : styles.deviceOff)}
                    onClick={handleAudio}
                  >
                    {isMicClosed ? <BsMicMuteFill style={{ fill: 'white' }} /> : <BsMic />}
                    <Badge
                      badgeContent={<IoAlert size={14} />}
                      sx={{
                        '& .MuiBadge-badge': {
                          position: 'absolute',
                          top: '-16px',
                          right: '-4px',
                          width: '16px',
                          height: '16px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: '#FFE07C',
                          color: '#090303',
                          borderRadius: '50%',
                          padding: 0,
                          minWidth: 0,
                        },
                      }}
                      invisible={isAudioAllowed}
                    />
                  </div>
                </OTooltip>
                {/* Cam */}
                <OTooltip
                  isCapitalized
                  title={`${isCamClosed ? 'open' : 'close'} camera`}
                  customStyles={customTooltipStyle}
                >
                  <div
                    className={clsx(styles.iconWrapper, !isCamClosed ? styles.transparentBg : styles.deviceOff)}
                    onClick={handleVideo}
                  >
                    {isCamClosed ? <BsCameraVideoOffFill style={{ fill: 'white' }} /> : <BsCameraVideo />}
                    <Badge
                      badgeContent={<IoAlert size={14} />}
                      sx={{
                        '& .MuiBadge-badge': {
                          position: 'absolute',
                          top: '-16px',
                          right: '-4px',
                          width: '16px',
                          height: '16px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: '#FFE07C',
                          color: '#090303',
                          borderRadius: '50%',
                          padding: 0,
                          minWidth: 0,
                        },
                      }}
                      invisible={isVideoAllowed}
                    />
                  </div>
                </OTooltip>
              </div>
              <div className={styles.settingsIconsWrapper}>
                <OTooltip title={'Background'}>
                  <div className={styles.virtualBackgroundIcon} onClick={handleOpenEffectsModal}>
                    <MdWaves height={14} width={14} className="white" />
                  </div>
                </OTooltip>
              </div>
              <video
                className={clsx(styles.videoPreview, {
                  hidden: isCamClosed || !originalStream,
                })}
                ref={setVideoRef}
                autoPlay
                muted
                playsInline
              />
              {!(originalStream && !isCamClosed) && (
                <div className={styles.avatarWrapper}>
                  <AvatarCircle name={peerName || 'Guest'} />
                </div>
              )}
            </div>
            <div className={styles.mediaDeviceFooter}>
              <div className={styles.deviceSelectWrapper}>
                {!!videoInputDevices.length && (
                  <OSelect
                    items={videoInputDevices.map(({ deviceId, label }) => ({
                      value: deviceId,
                      label,
                    }))}
                    onChange={(id) => {
                      const mediaDevice = videoInputDevices.find(({ deviceId }) => deviceId === id);
                      mediaDevice && handleChangeMediaDevice(mediaDevice, 'video');
                    }}
                    selectedValue={selectedCamera}
                    icon={<BsCameraVideo size={20} color="white" />}
                    size={oSelectSize}
                  />
                )}
                {!!audioInputDevices.length && (
                  <div>
                    <OSelect
                      items={audioInputDevices.map(({ deviceId, label }) => ({
                        value: deviceId,
                        label,
                      }))}
                      onChange={(id) => {
                        const mediaDevice = audioInputDevices.find(({ deviceId }) => deviceId === id);
                        mediaDevice && handleChangeMediaDevice(mediaDevice, 'audio');
                      }}
                      selectedValue={selectedAudio}
                      icon={<HiOutlineMicrophone size={20} color="white" />}
                      size={oSelectSize}
                    />
                  </div>
                )}
                {!!speakerDevices.length && (
                  <div>
                    <OSelect
                      items={speakerDevices.map(({ deviceId, label }) => ({
                        value: deviceId,
                        label,
                      }))}
                      onChange={(id) => {
                        const mediaDevice = speakerDevices.find(({ deviceId }) => deviceId === id);
                        mediaDevice && handleChangeMediaDevice(mediaDevice, 'speaker');
                      }}
                      selectedValue={selectedSpeaker}
                      icon={<PiSpeakerHigh size={20} color="white" />}
                      size={oSelectSize}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.lobbyRightSide}>
        {isWebinarStartedByHost || isHost ? (
          <div className={styles.webinarDetails}>
            <p className={styles.webinarTitle}>{roomTitle ?? 'Join the webinar'}</p>
            <p className={styles.webinarSubTitle}>Join the webinar</p>
            <div className={styles.joinMeeting}>
              <div>
                <OInput
                  className="user-name-input"
                  placeholder="Nickname"
                  value={inputValue}
                  onChange={(e) => handleChangeUsername(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleJoinWebinar();
                    }
                  }}
                  invalid={!!errors.username}
                />
                {errors && <span className={styles.invalid}>{errors.username}</span>}
              </div>
              {isPasswordRequired && (
                <div>
                  <OInput
                    className="password-input"
                    placeholder="Password"
                    type="password"
                    value={password}
                    onChange={handleChangePassword}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleJoinWebinar();
                      }
                    }}
                    invalid={!!joinErrors.password}
                  />
                  {joinErrors.password && <span className={styles.invalid}>{joinErrors.password}</span>}
                </div>
              )}
              <OTooltip
                title={
                  isSubmitButtonDisabled && isPasswordRequired && !password
                    ? 'Please enter your Nickname and Password first.'
                    : isSubmitButtonDisabled
                      ? 'Please enter your Nickname first.'
                      : isPasswordRequired && !password
                        ? 'Please enter your Password first.'
                        : ''
                }
                customStyles={customTooltipStyle}
                placement="top"
              >
                <div>
                  <OButton
                    variant="alternative"
                    size="large"
                    text="Join meeting"
                    loading={isJoinLoading}
                    isDisabled={isSubmitButtonDisabled || (isPasswordRequired && !password) || isJoinLoading}
                    onClick={handleJoinWebinar}
                  />
                </div>
              </OTooltip>
            </div>
          </div>
        ) : (
          <div className={styles.webinarDetails}>
            <p className={styles.webinarTitle}>v webinar meeting</p>
            <p className={styles.webinarDate}>{formatDateWithCity(date)}</p>
            <div className={styles.waitingForHostWrapper}>
              <p>Waiting for host to start the meeting</p>
              <div className={styles.loaderWrapper}>
                <LoaderIcon width={29} height={29} />
              </div>
            </div>
          </div>
        )}
      </div>
      <BackgroundEffectsSimpleModal
        isModalOpen={isBackgroundEffectsOpen}
        isApplyButtonDisabled={isApplyButtonDisabled || isCamClosed}
        selectedBackgroundEffect={selectedBackgroundEffect}
        onSelectBackgroundEffect={!isCamClosed ? onSelectBackgroundEffect : () => {}}
        removeBackgroundEffect={removeBackgroundEffect}
        handleApplyChanges={handleApplyChanges}
        handleCloseModal={handleCloseModal}
      />
      <InfoModal />
      <ToastContainer position="top-right" autoClose={3500} />
    </div>
  );
};

export default memo(WebinarLobbyWeb);
